import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import {
  getAdminJwt,
  getAdminToken,
  setAdminJwt,
  setAdminToken,
} from '../../utils/pricingContext.js';
import { decodeJwtPayload } from '../../utils/jwt.js';
import { siteConfigAdminService } from '../../services/admin-site-config.service.js';
import type { SiteConfig } from '../../types/index.js';
import './admin-shared.css';
import './ContentAdminPage.css';

const stringifyJson = (value: unknown) =>
  value === undefined ? '' : JSON.stringify(value, null, 2);

const parseJsonInput = (value: string, label: string): unknown | undefined => {
  if (!value.trim()) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    throw new Error(`${label} JSON is invalid`);
  }
};

const ContentAdminPage: React.FC = () => {
  const [jwtInput, setJwtInput] = useState(getAdminJwt() ?? '');
  const [tokenInput, setTokenInput] = useState(getAdminToken() ?? '');
  const [savedJwt, setSavedJwt] = useState(getAdminJwt() ?? '');
  const [savedToken, setSavedToken] = useState(getAdminToken() ?? '');
  const storeToken = useGlobalStore((s: GlobalState) => s.token);

  const [heroJson, setHeroJson] = useState('');
  const [servicesJson, setServicesJson] = useState('');
  const [featuredJson, setFeaturedJson] = useState('');
  const [specialsJson, setSpecialsJson] = useState('');
  const [dealsJson, setDealsJson] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<'info' | 'error'>('info');

  const tokenStatus = useMemo(() => {
    if (storeToken?.trim()) {
      const payload = decodeJwtPayload(storeToken);
      const roles = payload?.roles?.join(', ');
      return roles ? `IDP session active · Roles: ${roles}` : 'IDP session active';
    }
    if (savedJwt.trim()) {
      const payload = decodeJwtPayload(savedJwt);
      const roles = payload?.roles?.join(', ');
      return roles ? `JWT active · Roles: ${roles}` : 'JWT active';
    }
    if (savedToken.trim()) return 'Legacy admin token stored';
    return 'No admin auth configured yet';
  }, [savedJwt, savedToken, storeToken]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    setMessageTone('info');
    try {
      const config = await siteConfigAdminService.getConfig();
      setHeroJson(stringifyJson(config.hero));
      setServicesJson(stringifyJson(config.services));
      setFeaturedJson(stringifyJson(config.featuredCategories));
      setSpecialsJson(stringifyJson(config.specials));
      setDealsJson(stringifyJson(config.deals));
      setIsActive(config.isActive ?? true);
    } catch (error) {
      setMessageTone('error');
      setMessage(error instanceof Error ? error.message : 'Failed to load site config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSaveAuth = () => {
    setAdminJwt(jwtInput);
    setSavedJwt(jwtInput);
    setAdminToken(tokenInput);
    setSavedToken(tokenInput);
  };

  const handleSaveConfig = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    setMessageTone('info');
    try {
      const payload: Partial<SiteConfig> = {
        hero: parseJsonInput(heroJson, 'Hero'),
        services: parseJsonInput(servicesJson, 'Services'),
        featuredCategories: parseJsonInput(featuredJson, 'Featured Categories'),
        specials: parseJsonInput(specialsJson, 'Specials'),
        deals: parseJsonInput(dealsJson, 'Deals'),
        isActive,
      };
      const updated = await siteConfigAdminService.updateConfig(payload);
      setHeroJson(stringifyJson(updated.hero));
      setServicesJson(stringifyJson(updated.services));
      setFeaturedJson(stringifyJson(updated.featuredCategories));
      setSpecialsJson(stringifyJson(updated.specials));
      setDealsJson(stringifyJson(updated.deals));
      setIsActive(updated.isActive ?? true);
      setMessage('Site config saved');
    } catch (error) {
      setMessageTone('error');
      setMessage(error instanceof Error ? error.message : 'Failed to save site config');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-admin-page">
      <section className="page-hero content-admin-hero">
        <div className="container">
          <span className="badge badge-lime">Admin</span>
          <h1>Content & Specials</h1>
          <p>Control what appears across the site, including specials, services, and featured categories.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="admin-card">
            <h2>Access</h2>
            <p>Use a JWT access token for role-based access, or the legacy admin token.</p>
            <div className="admin-input-row">
              <input
                type="password"
                value={jwtInput}
                onChange={(event) => setJwtInput(event.target.value)}
                placeholder="Admin JWT (preferred)"
              />
              <button type="button" className="btn btn-primary" onClick={handleSaveAuth}>
                Save Auth
              </button>
            </div>
            <div className="admin-input-row">
              <input
                type="password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                placeholder="Legacy admin token (fallback)"
              />
            </div>
            <p className="status-message">{tokenStatus}</p>
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="container">
          <div className="admin-panel">
            <h2>Site Config</h2>
            <p className="muted">
              Edit JSON for each section. Leave a field blank to keep the current value, or enter <code>null</code> to clear it.
            </p>
            <form className="admin-form" onSubmit={handleSaveConfig}>
              <label className="admin-field">
                <span>Hero</span>
                <textarea
                  rows={6}
                  value={heroJson}
                  onChange={(event) => setHeroJson(event.target.value)}
                  placeholder='{"badge":"B2B Managed Technology"}'
                />
              </label>
              <label className="admin-field">
                <span>Services</span>
                <textarea
                  rows={6}
                  value={servicesJson}
                  onChange={(event) => setServicesJson(event.target.value)}
                  placeholder='[{"title":"Managed IT","description":"...","link":"/services/managed-services"}]'
                />
              </label>
              <label className="admin-field">
                <span>Featured Categories</span>
                <textarea
                  rows={6}
                  value={featuredJson}
                  onChange={(event) => setFeaturedJson(event.target.value)}
                  placeholder='[{"id":"laptops","name":"Business Laptops","searchText":"laptop"}]'
                />
              </label>
              <label className="admin-field">
                <span>Specials</span>
                <textarea
                  rows={6}
                  value={specialsJson}
                  onChange={(event) => setSpecialsJson(event.target.value)}
                  placeholder='{"title":"Curated Business Hardware","categories":[{"title":"Business Laptops","description":"..."}]}'
                />
              </label>
              <label className="admin-field">
                <span>Deals</span>
                <textarea
                  rows={4}
                  value={dealsJson}
                  onChange={(event) => setDealsJson(event.target.value)}
                  placeholder='{"enabled":true,"title":"Hot Deals"}'
                />
              </label>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
                Config active
              </label>
              <div className="admin-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Save Config
                </button>
                <button type="button" className="btn btn-outline" onClick={loadConfig}>
                  Reload
                </button>
              </div>
            </form>
            {loading && <p className="status-message">Saving...</p>}
            {message && (
              <p className={`status-message${messageTone === 'error' ? ' status-error' : ''}`}>
                {message}
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContentAdminPage;
